import React, { useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import mixpanel from "mixpanel-browser";

import { AuthContext } from "features/Auth";
import { usePaymentModal } from "contexts/PaymentModalContext.jsx";
import { useStateSelector } from "utils/field";

import Button from "components/Button";

import CreditsPurchased from "assets/images/icons/creditsPurchased.svg";
import CreditsInsights from "assets/images/icons/creditsInsights.svg";
import CreditsParametric from "assets/images/icons/creditsParametric.svg";

import classes from "./styles.module.scss";

const PRICES_LAST_UPDATED = "August 5, 2026";

const Pricing = () => {
    const history = useHistory();
    const { user } = useContext(AuthContext);
    const { dispatch: paymentModalDispatch } = usePaymentModal();
    const isUserVerified = useStateSelector("user.isUserVerified");

    useEffect(() => {
        mixpanel.track("Pricing Page View", {
            UserId: user?.uid || "",
            IsAuthenticated: Boolean(user),
        });
    }, [user?.uid]);

    const handlePrimaryAction = () => {
        if (user && !isUserVerified) return;

        mixpanel.track(user ? "Pricing Buy Credits Click" : "Pricing Sign Up Click", {
            UserId: user?.uid || "",
        });

        if (user) {
            paymentModalDispatch({ type: "OPEN" });
            return;
        }

        history.push("/register");
    };

    return (
        <main className={classes.pricingPage}>
            <section className={classes.hero}>
                <span className={classes.eyebrow}>Usage-based pricing</span>
                <h1>Only pay for the tools you use</h1>
                <p>
                    Core Volta SNAP modelling tools are free. Purchase credits when you need Insights or additional
                    Parametric or Community Analysis capacity.
                </p>
                <div className={classes.priceMeta}>
                    <p>Prices last updated on {PRICES_LAST_UPDATED}</p>
                    <p>Prices and credit rates may change with 30 days' notice by email and in-app notification.</p>
                </div>
            </section>

            <section className={classes.priceCard}>
                <div className={classes.priceIcon}>
                    <img src={CreditsPurchased} alt="" />
                </div>
                <div className={classes.priceDetails}>
                    <p className={classes.priceLabel}>Volta SNAP credits</p>
                    <div className={classes.price}>
                        <strong>$5</strong>
                        <span>CAD per credit, plus applicable taxes</span>
                    </div>
                    <p>Purchase up to 100 credits at a time.</p>
                </div>
                <Button onClick={handlePrimaryAction} large disabled={Boolean(user) && !isUserVerified}>
                    {user ? "Buy credits" : "Create an account"}
                </Button>
            </section>

            <section className={classes.section}>
                <div className={classes.sectionHeading}>
                    <h2>How credits are used</h2>
                    <p>Credits remain in your account until you choose to spend them.</p>
                </div>

                <div className={classes.usageGrid}>
                    <article className={classes.usageCard}>
                        <img src={CreditsInsights} alt="" />
                        <div>
                            <p className={classes.cardKicker}>Insights</p>
                            <h3>1 credit per model</h3>
                            <p>
                                Permanently unlock Room-by-Room, EnergyPlus, and Utility Analysis for one model. The
                                first parametric analysis created from that model also includes 2,500 unlocked runs.
                            </p>
                        </div>
                    </article>

                    <article className={classes.usageCard}>
                        <img src={CreditsParametric} alt="" />
                        <div>
                            <p className={classes.cardKicker}>Parametric &amp; Community Analysis</p>
                            <h3>1 credit per 2,500 runs</h3>
                            <p>
                                Unlock analysis capacity in blocks of 2,500 runs. Capacity stays with that analysis and
                                can be increased whenever more combinations are needed.
                            </p>
                        </div>
                    </article>
                </div>
            </section>

            <section className={classes.detailsSection}>
                <h2>Good to know</h2>
                <div className={classes.detailsGrid}>
                    <div>
                        <h3>No subscription required</h3>
                        <p>There is no recurring charge. Buy credits only when you need paid features.</p>
                    </div>
                    <div>
                        <h3>Core modelling remains free</h3>
                        <p>Running HOT2000 simulations or generating whole-home F280 results do not require credits.</p>
                    </div>
                    <div>
                        <h3>Organization transfers</h3>
                        <p>Organization administrators can transfer available credits to other organization members.</p>
                    </div>
                    <div>
                        <h3>Credits are non-refundable</h3>
                        <p>Credits cannot be recovered after they have been spent to unlock a feature or runs.</p>
                    </div>
                </div>
            </section>

            <section className={classes.faqSection}>
                <h2>Frequently asked questions</h2>
                <div className={classes.faqList}>
                    <details>
                        <summary>Do credits expire?</summary>
                        <p>Credits remain available in your account until they are spent.</p>
                    </details>
                    <details>
                        <summary>What happens if I duplicate an Insights model?</summary>
                        <p>
                            Insights activation belongs to the original model. A duplicate must be activated separately.
                        </p>
                    </details>
                    <details>
                        <summary>How do legacy credits work?</summary>
                        <p>
                            Legacy credits (credits purchased prior to version 1.0.0) can unlock parametric runs at a
                            1:1 rate, or be converted to new credits at 2,500 legacy credits to 1 new credit, rounded
                            up. Conversion is irreversible.
                        </p>
                    </details>
                    <details>
                        <summary>Are any Insights features available as a trial?</summary>
                        <p>
                            Some Insights features may be available without activation during a limited launch trial.
                            Activating Insights provides permanent access for that model.
                        </p>
                    </details>
                </div>
            </section>
        </main>
    );
};

export default Pricing;
